import React from "react";
import Spinner from "../../../../components/Spinner";
import EnvironmentPill from "../../../../components/EnvironmentPill";
import EyeIcon from "../../../../components/EyeIcon";
import {
  formatIdpLoginButtonLabel,
  signInOptionPickerKey,
  signInOptionUsesLocalPassword,
} from "./signInOptions.js";

const shellClass =
  "w-full max-w-[560px] sm:max-w-[520px] md:max-w-[600px] lg:max-w-[560px] rounded-[18px] border border-black/5 bg-white p-6 sm:p-8 md:p-12 lg:p-10 shadow-[0_18px_44px_rgba(0,0,0,0.14)]";

export default function SSOLoginMainPanel(props) {
  const {
    brand,
    isAdminAccess,
    showAdminOktaSilentSplash,
    successMessage,
    error,
    isLoading,
    email,
    emailError,
    onEmailChange,
    onEmailBlur,
    showSignInChooser,
    signInChooserNeedsHeading,
    noSignInMethodConfigured,
    distinctSignInOptions,
    selectedIdpConfig,
    setSelectedIdpConfig,
    startCustomerSsoRedirect,
    showPasswordFields,
    localPassword,
    setLocalPassword,
    showLocalPassword,
    setShowLocalPassword,
    passwordError,
    showForgotPasswordLink,
    handleForgotPasswordClick,
    handleEmailSubmit,
    primaryButtonLabel,
    primaryButtonDisabled,
    usePlatformAdminFlow,
    platformUseOkta,
    setError,
    setPasswordError,
  } = props;

  if (showAdminOktaSilentSplash) {
    return (
      <div className={shellClass} role="main" aria-busy="true" aria-live="polite">
        <div className="text-center py-6 sm:py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-700 mx-auto" aria-hidden="true" />
          <p className="mt-4 text-slate-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={shellClass} role="main" aria-labelledby="login-title">
      <h1 id="login-title" className="text-xl sm:text-2xl md:text-3xl lg:text-[38px] font-extrabold text-[#3A4A51] leading-tight">
        Welcome back to {isAdminAccess ? "OneDoor Admin Portal" : `${brand.product_alias} Portal`}!
      </h1>

      <div className="mt-4 flex justify-center">
        <EnvironmentPill />
      </div>

      {successMessage && (
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-md animate-notice-slide-in" role="status">
          <p className="text-sm text-green-800">{successMessage}</p>
        </div>
      )}
      {error && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md animate-notice-slide-in">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleEmailSubmit} className="mt-8">
        <div className="space-y-6">
          <div>
            <label htmlFor="email" className="text-sm sm:text-base font-extrabold tracking-wider text-[#2B7F8D]">
              EMAIL ADDRESS
            </label>
            <div className="relative mt-2">
              <input
                id="email"
                type="email"
                value={email}
                aria-describedby={emailError ? "email-error" : undefined}
                onChange={(e) => onEmailChange(e.target.value)}
                onBlur={(e) => onEmailBlur(e.target.value)}
                className={`h-12 sm:h-[54px] w-full rounded-[4px] border-2 px-3 sm:px-4 text-sm sm:text-base outline-none ${
                  emailError ? "border-red-400 focus:border-red-500" : "border-slate-300 focus:border-[#2B7F8D]"
                }`}
                placeholder="Enter your email address"
                disabled={isLoading}
                autoComplete="email"
                required
              />
            </div>
            {emailError && (
              <p id="email-error" className="mt-2 text-sm text-red-600" role="alert">
                {emailError}
              </p>
            )}
          </div>

          {noSignInMethodConfigured && (
            <div
              className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
              role="status"
            >
              No sign-in method is configured for this email domain. Try another address or contact your administrator.
            </div>
          )}

          {showSignInChooser && !selectedIdpConfig && (
            <div>
              {signInChooserNeedsHeading && (
                <p className="text-sm sm:text-base font-extrabold tracking-wider text-[#2B7F8D] mb-2">
                  Choose how to sign in
                </p>
              )}
              <div className="flex flex-col gap-2">
                {distinctSignInOptions.map((config) => {
                  const label = formatIdpLoginButtonLabel(config);
                  const isLocal = signInOptionUsesLocalPassword(config);
                  return (
                    <button
                      key={signInOptionPickerKey(config)}
                      type="button"
                      disabled={isLoading}
                      onClick={() => {
                        if (isLocal) {
                          setSelectedIdpConfig(config);
                          setError(null);
                        } else {
                          startCustomerSsoRedirect(config);
                        }
                      }}
                      className="h-12 rounded-[4px] border-2 border-slate-300 px-4 text-left text-sm sm:text-base font-extrabold tracking-wider text-[#2B7F8D] hover:border-[#2B7F8D] hover:bg-slate-50 focus:ring-2 focus:ring-[#2B7F8D] focus:ring-offset-2 disabled:opacity-50"
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {showPasswordFields && (
            <div>
              <label htmlFor="local-password" className="text-sm sm:text-base font-extrabold tracking-wider text-[#2B7F8D]">
                PASSWORD
              </label>
              <div className="relative mt-2">
                <input
                  id="local-password"
                  type={showLocalPassword ? "text" : "password"}
                  value={localPassword}
                  onChange={(e) => {
                    setLocalPassword(e.target.value);
                    setPasswordError("");
                    setError(null);
                  }}
                  className={`h-12 sm:h-[54px] w-full rounded-[4px] border-2 px-3 sm:px-4 pr-12 text-sm sm:text-base outline-none ${
                    passwordError ? "border-red-400 focus:border-red-500" : "border-slate-300 focus:border-[#2B7F8D]"
                  }`}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  autoComplete="current-password"
                  aria-describedby={passwordError ? "password-error" : undefined}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-2 focus:outline-none focus:ring-2 focus:ring-[#2B7F8D] focus:ring-offset-2"
                  onClick={() => setShowLocalPassword((current) => !current)}
                  aria-label={showLocalPassword ? "Hide password" : "Show password"}
                  aria-pressed={showLocalPassword}
                  disabled={isLoading}
                >
                  <EyeIcon open={showLocalPassword} />
                </button>
              </div>
              {passwordError && (
                <p id="password-error" className="mt-2 text-sm text-red-600" role="alert">
                  {passwordError}
                </p>
              )}
              {distinctSignInOptions.length > 1 && selectedIdpConfig && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => setSelectedIdpConfig(null)}
                    className="text-sm text-slate-600 underline hover:text-slate-800"
                  >
                    Choose a different sign-in option
                  </button>
                </div>
              )}
              {showForgotPasswordLink && (
                <div className="mt-3 text-right">
                  <button
                    type="button"
                    onClick={handleForgotPasswordClick}
                    className="text-[16px] font-semibold text-[#2B7F8D] underline focus:outline-none focus:ring-2 focus:ring-[#2B7F8D] focus:ring-offset-2 rounded px-1 py-0.5"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={primaryButtonDisabled}
            className="h-12 sm:h-[54px] w-full rounded-[4px] bg-[#2B7F8D] text-sm sm:text-base font-bold text-white outline-none hover:bg-[#256f7a] focus:ring-2 focus:ring-[#2B7F8D] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400 transition-colors"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-3">
                <Spinner size={24} />
                <span>{usePlatformAdminFlow && platformUseOkta ? "Redirecting..." : "Signing in..."}</span>
              </span>
            ) : (
              primaryButtonLabel
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
